import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, ArrowLeft, CheckCircle2, X, Heart, Award } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionCard = ({ title, children, accent = "var(--gold)" }) => (
  <div className="panel p-5 sm:p-6 mb-4">
    <h2 className="font-display text-lg sm:text-xl mb-3" style={{ color: accent }}>{title}</h2>
    <div className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
      {children}
    </div>
  </div>
);

const SampleImage = ({ src, label, caption }) => (
  <figure className="rounded overflow-hidden" style={{ background: "var(--bg-deep)" }}>
    {src ? (
      <img src={src} alt={label} className="w-full aspect-video object-cover" loading="lazy" />
    ) : (
      <div className="w-full aspect-video flex items-center justify-center" style={{ color: "var(--text-muted)" }}>
        <Camera size={24} />
      </div>
    )}
    <figcaption className="px-3 py-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
      <b style={{ color: "var(--text-primary)" }}>{label}</b>
      {caption && <span className="ml-2 opacity-70">{caption}</span>}
    </figcaption>
  </figure>
);

const Bullet = ({ children, type = "ok" }) => (
  <li className="flex items-start gap-2 mb-1.5">
    {type === "ok" ? (
      <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--green-success)" }} />
    ) : (
      <X size={14} className="mt-0.5 shrink-0" style={{ color: "var(--red-warn)" }} />
    )}
    <span>{children}</span>
  </li>
);

const ContributeGuide = () => {
  const [samples, setSamples] = useState({});

  useEffect(() => {
    document.title = "撮影ガイド｜三送会動画素材投稿";
    axios.get(`${API_BASE}/contribute/guide_samples`).then(r => setSamples(r.data)).catch(() => {});
  }, []);

  const s = (key) => samples[key]?.src_medium || null;

  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: "var(--bg-deep)" }} data-testid="guide-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        {/* Header */}
        <Link to="/upload" className="inline-flex items-center gap-1 text-xs mb-4" style={{ color: "var(--gold)" }} data-testid="back-to-upload">
          <ArrowLeft size={14} /> 投稿ページに戻る
        </Link>

        <div className="text-center mb-8">
          <img src="/rushers_logo.png" alt="Rushers" className="mx-auto mb-4" style={{ maxWidth: "140px", height: "auto" }} />
          <h1 className="font-display text-3xl sm:text-4xl mb-2">三送会動画素材<br className="sm:hidden" />ご協力のお願い</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            撮影ガイド・お願い文
          </p>
        </div>

        {/* 1. 目的 */}
        <SectionCard icon={Heart} title="1. 目的">
          <p className="mb-3">
            三送会で、3年生のラストイヤーを振り返る動画を上映する予定です。
          </p>
          <p className="mb-3">
            そのため、各ご家庭でご子息の動画素材を、可能な範囲で撮りためていただけますと大変ありがたいです。
          </p>
          <p className="mb-3">
            この動画では、<b style={{ color: "var(--gold)" }}>試合中の大きなプレーだけでなく、試合前後の表情や、仲間と過ごす自然な場面、最後の一年の空気感</b>も大切にしたいと考えています。
          </p>
          <p>
            うまく撮れているかどうかは気にせず、まずは残していただくことを大事にしたいです。
          </p>
        </SectionCard>

        {/* 2. 撮影の基本ルール */}
        <SectionCard icon={Camera} title="2. 撮影の基本ルール">
          <ul className="space-y-1">
            <Bullet>ご自身のご子息を中心に撮影してください</Bullet>
            <Bullet>スマートフォンで問題ありません</Bullet>
            <Bullet><b>できるだけ横向き</b>で撮影してください</Bullet>
            <Bullet>1本あたり<b>5〜10秒ほど、少し長めに</b>撮ってください</Bullet>
            <Bullet>編集や加工はせず、元データのままで大丈夫です</Bullet>
            <Bullet>ズームしすぎず、顔や背番号が分かるくらいで十分です</Bullet>
            <Bullet>プレーだけでなく、表情や後ろ姿、仲間とのやり取りもぜひ撮ってください</Bullet>
            <Bullet>音も大切なので、そのまま撮影してください</Bullet>
          </ul>
        </SectionCard>

        {/* 3. 試合前 */}
        <SectionCard icon={Award} title="3. 特に撮っていただきたい場面 — 試合前">
          <p className="mb-3 text-xs" style={{color: "var(--gold)"}}>
            ※ ご自身のご子息を中心に、こういった構図で撮っていただけると助かります。
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SampleImage src={s("before_game")} label="ご子息の試合前の表情" caption="集中した顔" />
            <SampleImage src={s("helmet")} label="ヘルメットをかぶる瞬間" caption="顔・装備の寄り" />
          </div>
          <ul className="space-y-1">
            <Bullet>会場到着の様子</Bullet>
            <Bullet>ユニフォーム姿</Bullet>
            <Bullet>ヘルメットをかぶる瞬間</Bullet>
            <Bullet>テーピングやグローブを整える場面</Bullet>
            <Bullet>アップ、ストレッチ、走り出し</Bullet>
            <Bullet>整列前の表情</Bullet>
            <Bullet>円陣の前後</Bullet>
            <Bullet>仲間と話している場面</Bullet>
            <Bullet>コーチの話を聞く様子</Bullet>
          </ul>
        </SectionCard>

        {/* 4. 試合中 */}
        <SectionCard icon={Award} title="4. 試合中">
          <p className="mb-3 text-xs" style={{color: "var(--gold)"}}>
            ※ 遠景より、ご子息の動き・表情・背番号が見える構図が嬉しいです。
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SampleImage src={s("play_action")} label="ご子息のプレー" caption="走る・タックル等" />
            <SampleImage src={s("huddle")} label="背番号が見えるカット" caption="後ろ姿でもOK" />
          </div>
          <ul className="space-y-1">
            <Bullet>プレー中のご子息</Bullet>
            <Bullet>ベンチで待機している様子</Bullet>
            <Bullet>仲間と声を掛け合っている場面</Bullet>
            <Bullet>ハドルに入っている場面</Bullet>
            <Bullet>プレー後の表情</Bullet>
            <Bullet>良いプレーの直後</Bullet>
            <Bullet>苦しい場面のあと立て直している様子</Bullet>
            <Bullet>サイドラインで応援している姿</Bullet>
            <Bullet>背番号がしっかり見える場面</Bullet>
          </ul>
        </SectionCard>

        {/* 5. 試合後 */}
        <SectionCard icon={Award} title="5. 試合後">
          <p className="mb-3 text-xs" style={{color: "var(--gold)"}}>
            ※ 試合終了直後のご子息の表情・後ろ姿が、動画全体で大切な素材になります。
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SampleImage src={s("after_game")} label="ご子息の試合後の表情" caption="自然な感情" />
            <SampleImage src={s("back")} label="ヘルメットを持つ後ろ姿" caption="フィールドを離れる" />
          </div>
          <ul className="space-y-1">
            <Bullet>試合終了直後の表情</Bullet>
            <Bullet>整列、礼、握手</Bullet>
            <Bullet>仲間と話している場面</Bullet>
            <Bullet>悔しそうな顔、ほっとした顔</Bullet>
            <Bullet>コーチの話を聞く様子</Bullet>
            <Bullet>最後のハドル</Bullet>
            <Bullet>ヘルメットを持って歩く後ろ姿</Bullet>
            <Bullet>試合後しばらくの自然な表情</Bullet>
          </ul>
        </SectionCard>

        {/* 6. 練習・日常 */}
        <SectionCard icon={Award} title="6. 練習や日常">
          <p className="mb-3 text-xs" style={{color: "var(--gold)"}}>
            ※ ご子息個人の動き・表情を中心に撮っていただけると活きます。
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SampleImage src={s("training")} label="ご子息の練習風景" caption="走り込み・基礎練" />
            <SampleImage src={s("smile")} label="ご子息の自然な笑顔" caption="日常の一瞬" />
          </div>
          <ul className="space-y-1">
            <Bullet>ランニング</Bullet>
            <Bullet>基礎練習</Bullet>
            <Bullet>ウェイト</Bullet>
            <Bullet>ミーティング</Bullet>
            <Bullet>給水している場面</Bullet>
            <Bullet>笑顔</Bullet>
            <Bullet>仲間と話している場面</Bullet>
            <Bullet>準備や片付け</Bullet>
            <Bullet>移動中</Bullet>
            <Bullet>帰るときの後ろ姿</Bullet>
          </ul>
        </SectionCard>

        {/* 7. 価値が高い素材 */}
        <SectionCard icon={Heart} title="7. 特に価値が高い素材">
          <p className="mb-3">次のような<b style={{color: "var(--gold)"}}>ご子息個人の何気ない瞬間</b>は、動画の中でとても活きます。</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SampleImage src={s("macro")} label="ご子息の手元・装備の寄り" caption="テープ・グローブ" />
            <SampleImage src={s("bench")} label="ベンチでのご子息" caption="仲間と声をかける瞬間" />
          </div>
          <ul className="space-y-1">
            <Bullet>試合前の静かな表情</Bullet>
            <Bullet>ベンチで仲間と声を掛け合う場面</Bullet>
            <Bullet>試合後の表情</Bullet>
            <Bullet>ヘルメットをかぶる瞬間</Bullet>
            <Bullet>背番号が見えるカット</Bullet>
            <Bullet>後ろ姿</Bullet>
            <Bullet>笑顔</Bullet>
            <Bullet>親から見た自然な一瞬</Bullet>
          </ul>
        </SectionCard>

        {/* 8. 各試合最低6本 */}
        <SectionCard icon={CheckCircle2} title="8. 各試合で最低限お願いしたい6本" accent="var(--green-success)">
          <p className="mb-3">各試合ごとに、できれば次の6本をお願いしたいです。この6本があるだけでもかなり助かります。</p>
          <ol className="space-y-2 list-decimal list-inside">
            <li><b>試合前の表情</b> 1本</li>
            <li><b>アップまたは円陣</b> 1本</li>
            <li><b>プレー中</b> 1本</li>
            <li><b>ベンチやハドル</b> 1本</li>
            <li><b>試合終了直後</b> 1本</li>
            <li><b>試合後の後ろ姿または自然な表情</b> 1本</li>
          </ol>
        </SectionCard>

        {/* 9. 最後の大会 */}
        <SectionCard icon={Heart} title="9. 最後の大会で特にお願いしたいこと" accent="#E27D60">
          <p className="mb-3">最後の大会だけは、次の場面を特にお願いしたいです。</p>
          <ul className="space-y-1 mb-4">
            <Bullet>試合前30分くらいの様子</Bullet>
            <Bullet>ロッカールームや整列前の雰囲気</Bullet>
            <Bullet>試合中のプレー</Bullet>
            <Bullet>ベンチやスタンドの緊張感</Bullet>
            <Bullet>試合終了直後</Bullet>
            <Bullet>最後のハドル</Bullet>
            <Bullet>整列、握手、礼</Bullet>
            <Bullet>試合後しばらくの表情</Bullet>
            <Bullet>ヘルメットを持って歩く背中</Bullet>
          </ul>
          <p className="p-3 rounded text-xs" style={{ background: "rgba(226,125,96,0.1)", border: "1px solid rgba(226,125,96,0.3)", color: "#E27D60" }}>
            <b>※ 最後の試合だけは、試合後もしばらくカメラを止めずに撮っていただけると、とてもありがたいです。</b>
          </p>
        </SectionCard>

        {/* 10. 提出時のお願い */}
        <SectionCard icon={CheckCircle2} title="10. 提出時のお願い">
          <ul className="space-y-1 mb-3">
            <Bullet>元データのままお送りください</Bullet>
            <Bullet>できるだけ画質を落とさずに共有してください</Bullet>
            <Bullet>ファイル名が付けられる場合は、日付と名前が分かる形だと助かります</Bullet>
          </ul>
          <p className="text-xs p-3 rounded" style={{ background: "var(--bg-deep)", color: "var(--text-muted)" }}>
            例：<br />
            ・2026-09-20_山田太郎_秋大会試合前<br />
            ・2026-10-03_山田太郎_練習<br />
            ・2026-12-05_山田太郎_最後の試合後
          </p>
          <p className="text-[11px] mt-2" style={{ color: "var(--gold)" }}>
            📱 ファイル名はそのままでもOKです（自動でリネームされます）。
          </p>
        </SectionCard>

        {/* CTA */}
        <div className="text-center my-8">
          <Link
            to="/upload"
            className="btn-gold inline-flex"
            data-testid="cta-upload"
          >
            <Camera size={14} /> 投稿ページへ
          </Link>
        </div>

        <p className="text-[10px] text-center mt-6" style={{ color: "var(--text-muted)" }}>
          ※ サンプル写真は Pexels の無料素材です（イメージ）
        </p>
      </motion.div>
    </div>
  );
};

export default ContributeGuide;
